import { useCallback, useRef, useEffect } from 'react';
import { knowledgeApi, KnowledgeItem } from '../services/api';
import { createDebouncedFunction } from '../utils/debounceUtils';

interface UseFileRenameProps {
  selectedItem: KnowledgeItem | null;
  setItems: React.Dispatch<React.SetStateAction<KnowledgeItem[]>>;
  setSelectedItem: React.Dispatch<React.SetStateAction<KnowledgeItem | null>>;
}

interface UseFileRenameReturn {
  handleTitleInputChange: (newTitle: string) => void;
  handleTitleUpdate: (newTitle: string) => void;
  cleanup: () => void;
}

/**
 * Хук для управления переименованием файлов с debounce
 */
export const useFileRename = ({
  selectedItem,
  setItems,
  setSelectedItem
}: UseFileRenameProps): UseFileRenameReturn => {
  const titleEditTimer = useRef<NodeJS.Timeout | null>(null);

  // Функция для обновления названия в локальном состоянии
  const updateLocalTitle = useCallback((newTitle: string) => {
    if (!selectedItem) return;

    const safeTitle = newTitle.trim() || selectedItem.name;

    // Обновляем items
    setItems(prev => {
      const updateItemName = (items: KnowledgeItem[]): KnowledgeItem[] => {
        return items.map(item => {
          if (item.id === selectedItem.id) {
            return {
              ...item,
              name: safeTitle
            };
          }
          if (item.children && item.children.length > 0) {
            return {
              ...item,
              children: updateItemName(item.children)
            };
          }
          return item;
        });
      };
      return updateItemName(prev);
    });

    // Обновляем selectedItem
    setSelectedItem(prev => {
      if (prev) {
        return {
          ...prev,
          name: safeTitle
        };
      }
      return prev;
    });
  }, [selectedItem, setItems, setSelectedItem]);

  // Функция для сохранения названия на сервере
  const saveTitleToServer = useCallback(async (newTitle: string) => {
    if (!selectedItem || selectedItem.id.startsWith('temp-') || !newTitle.trim()) {
      return;
    }

    try {
      console.log('[useFileRename] Автоматическое сохранение названия файла:', newTitle);
      
      // Используем специализированный метод API для переименования
      const result = await knowledgeApi.renameItem(selectedItem.id, newTitle.trim());
      
      console.log('[useFileRename] Название файла успешно сохранено на сервере:', result);
    } catch (error) {
      console.error('[useFileRename] Ошибка при сохранении названия файла:', error);
      
      // В случае ошибки возвращаем прежнее имя
      if (selectedItem) {
        setItems(prev => {
          const revertItemName = (items: KnowledgeItem[]): KnowledgeItem[] => {
            return items.map(item => {
              if (item.id === selectedItem.id) {
                return {
                  ...item,
                  name: selectedItem.name
                };
              }
              if (item.children && item.children.length > 0) {
                return {
                  ...item,
                  children: revertItemName(item.children)
                };
              }
              return item;
            });
          };
          return revertItemName(prev);
        });
        
        setSelectedItem(prev => {
          if (prev) {
            return {
              ...prev,
              name: selectedItem.name
            };
          }
          return prev;
        });
      }
    }
  }, [selectedItem, setItems, setSelectedItem]);

  // Создаем debounced функцию для сохранения
  const { debouncedFunc: debouncedSave, cancel: cancelDebouncedSave } = createDebouncedFunction(
    saveTitleToServer,
    1000 // 1 секунда задержка
  );

  // Обработчик для мгновенного обновления заголовка с debounce для сохранения
  const handleTitleInputChange = useCallback((newTitle: string) => {
    if (!selectedItem) return;

    // Обновляем UI мгновенно
    updateLocalTitle(newTitle);

    // Отменяем предыдущий таймер и устанавливаем новый
    cancelDebouncedSave();
    debouncedSave(newTitle);
  }, [selectedItem, updateLocalTitle, debouncedSave, cancelDebouncedSave]);

  // Обработчик для мгновенного сохранения (например, при нажатии Enter)
  const handleTitleUpdate = useCallback(async (newTitle: string) => {
    if (!selectedItem) return;

    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) {
      console.log("useFileRename: Попытка сохранить пустое название, игнорируем");
      return;
    }

    // Проверка на временный ID
    if (selectedItem.id.startsWith('temp-')) {
      console.log("useFileRename: обновление временного файла:", selectedItem.id);
      updateLocalTitle(trimmedTitle);
      return;
    }

    // Обновляем UI мгновенно
    updateLocalTitle(trimmedTitle);

    // Отменяем debounced сохранение
    cancelDebouncedSave();

    // Сохраняем немедленно
    try {
      console.log("useFileRename: отправляем запрос на сохранение заголовка:", selectedItem.id, trimmedTitle);
      await knowledgeApi.renameItem(selectedItem.id, trimmedTitle);
      console.log("useFileRename: заголовок успешно обновлен на сервере");
    } catch (error) {
      console.error("useFileRename: ошибка при обновлении заголовка:", error);
      
      // В случае ошибки возвращаем прежнее имя
      if (selectedItem) {
        setItems(prev => {
          const revertItemName = (items: KnowledgeItem[]): KnowledgeItem[] => {
            return items.map(item => {
              if (item.id === selectedItem.id) {
                return {
                  ...item,
                  name: selectedItem.name
                };
              }
              if (item.children && item.children.length > 0) {
                return {
                  ...item,
                  children: revertItemName(item.children)
                };
              }
              return item;
            });
          };
          return revertItemName(prev);
        });
        
        setSelectedItem(prev => {
          if (prev) {
            return {
              ...prev,
              name: selectedItem.name
            };
          }
          return prev;
        });
      }
    }
  }, [selectedItem, updateLocalTitle, cancelDebouncedSave, setItems, setSelectedItem]);

  // Функция очистки
  const cleanup = useCallback(() => {
    if (titleEditTimer.current) {
      clearTimeout(titleEditTimer.current);
      titleEditTimer.current = null;
    }
    cancelDebouncedSave();
  }, [cancelDebouncedSave]);

  // Очистка при размонтировании
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    handleTitleInputChange,
    handleTitleUpdate,
    cleanup
  };
};
